import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Button, useTheme, Snackbar } from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EvidenceSetSidebar from "../components/Drawers/EvidenceSetSidebar";
import EvidenceSetDetails from "../components/Drawers/EvidenceSetDetails";
import ControlCommentsDrawer from "../components/Drawers/ControlCommentsDrawer";
import EvidenceRequestDialog from "../components/Dialogs/EvidenceRequestDialog";
import ReadyForAuditDialog from "../components/Dialogs/ReadyForAuditDialog";
import ControlHeaderCard from "../features/control-details/ControlHeaderCard";
import ControlActionButtons from "../features/control-details/ControlActionButtons";
import LoadingToast from "../../../../components/LoadingToast";
import { useControlDetails } from "../hooks/useControlDetails";
import { useControlStatus } from "../hooks/useControlStatus";
import { GET_CONTROL_COMMENTS } from "../../../../apis/settingsApis/audit-apis/commentApis";
import { CREATE_EVIDENCE_REQUEST } from "../../../../apis/settingsApis/audit-apis/evidenceRequestApis";
import { EXPORT_CONTROL_EVIDENCE } from "../../../../apis/organization-apis/evidence-apis/evidenceApis";
import { GET_AUDITS } from "../../../../apis/settingsApis/audit-apis/auditApis";
import {
  AnnotationCommentEntityType,
  CommentModuleTypeEnum,
  AdminStatusType,
} from "../../../../utils/enums";
import { downloadFromUrl } from "../../../../utils/downloadUtils";


const ControlDetailsPage = () => {
  const theme = useTheme();
  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
  const [controlDetailsExpanded, setControlDetailsExpanded] = useState(false);
  const [evidenceRequestDialogOpen, setEvidenceRequestDialogOpen] = useState(false);
  const [readyForAuditDialogOpen, setReadyForAuditDialogOpen] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [isDownloadingEvidence, setIsDownloadingEvidence] = useState(false);
  const [selectedRunDate, setSelectedRunDate] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { auditId, controlId } = useParams();

  const isAuditorView = location.pathname.startsWith("/auditor/");
  const basePath = isAuditorView ? "/auditor" : "/admin";

  const audit = location.state?.audit;

  // Fetch audit if not in navigation state (email links)
  const { data: fetchedAuditData, loading: auditLoading } = useQuery(GET_AUDITS, {
    variables: { page: 1, limit: 1, filter: { id: auditId } },
    skip: !!audit,  // Skip if audit already available
    fetchPolicy: "cache-first",
  });

  // Use audit from state or fetched data
  const auditData = audit || fetchedAuditData?.getAudits?.data?.[0];

  const {
    control,
    sortedChecks,
    selectedCheckType,
    setSelectedCheckType,
    checkDetails,
    activeCheckId,
    checkCategory,
    isLoading,
    checkLoading,
    refetchCheckDetails,
  } = useControlDetails(auditData, controlId, auditId, selectedRunDate);

  const {
    currentStatusKey,
    redFlag,
    statusLoading,
    isSubmittingStatus,
    isSubmittingRedFlag,
    handleStatusUpdate,
    handleRedFlagToggle,
    handleRemoveStatus,
  } = useControlStatus(control, auditData, isAuditorView, setSnackbarInfo);

  const { data: commentsData, refetch: refetchCommentsCount } = useQuery(GET_CONTROL_COMMENTS, {
    variables: {
      filter: {
        module_type: CommentModuleTypeEnum.AUDIT,
        module_id: auditData?.id,
        entity_type: AnnotationCommentEntityType.CONTROL,
        entity_id: control?.auditControlId,
      },
      page: 1,
      limit: 1,
    },
    skip: !control?.auditControlId || !auditData?.id,
    fetchPolicy: "cache-and-network",
  });

  const commentsCount = commentsData?.getAnnotationComments?.totalCount || 0;

  const [createEvidenceRequest, { loading: creatingEvidenceRequest }] = useMutation(
    CREATE_EVIDENCE_REQUEST,
    {
      onCompleted: (data) => {
        if (data?.createEvidenceRequest?.success) {
          setSnackbarInfo({
            message: "Evidence request created successfully!",
            vertical: "top",
            horizontal: "right",
          });
          setEvidenceRequestDialogOpen(false);
        } else {
          setSnackbarInfo({
            message: data?.createEvidenceRequest?.message || "Failed to create evidence request",
            vertical: "top",
            horizontal: "right",
          });
        }
      },
      onError: (error) => {
        setSnackbarInfo({
          message: error.message || "An error occurred while creating evidence request",
          vertical: "top",
          horizontal: "right",
        });
      },
    }
  );

  const [exportControlEvidence] = useMutation(EXPORT_CONTROL_EVIDENCE);

  const handleCheckClick = (check) => {
    setSelectedCheckType(check.checkType || check.activeCheckName);
  };

  const handleBack = () => {
    navigate(`${basePath}/audits/${auditId}`, { state: { activeTab: 2 } });
  };

  const handleCommentsClick = () => {
    setCommentsDrawerOpen(true);
  };

  const handleCommentsClose = () => {
    setCommentsDrawerOpen(false);
    refetchCommentsCount();
  };

  const handleEvidenceRequestClick = () => {
    setEvidenceRequestDialogOpen(true);
  };

  const handleEvidenceRequestClose = () => {
    setEvidenceRequestDialogOpen(false);
  };

  const handleEvidenceRequestSubmit = (data) => {
    createEvidenceRequest({
      variables: {
        input: {
          audit_id: auditData?.id,
          entity_type: data.entity_type,
          entity_id: data.entity_id,
          evidence_requested_reason: data.evidence_requested_reason,
        },
      },
    });
  };

  const handleReadyForAuditClick = () => {
    setReadyForAuditDialogOpen(true);
  };

  const handleReadyForAuditConfirm = async () => {
    await handleStatusUpdate(AdminStatusType.READY_FOR_AUDIT);
    setReadyForAuditDialogOpen(false);
    setSnackbarInfo({
      message: "Control marked as ready for audit successfully!",
      vertical: "top",
      horizontal: "right",
    });
  };

  const handleDownloadEvidence = async () => {
    setIsDownloadingEvidence(true);
    try {
      const result = await exportControlEvidence({
        variables: {
          controlId: parseInt(controlId),
          auditId: auditData?.id || auditId,
          end_time: auditData?.audit_end_date,
        },
      });

      const response = result?.data?.exportControlEvidence;
      if (response?.success && response?.data?.downloadUrl) {
        await downloadFromUrl(
          response.data.downloadUrl,
          response.data.fileName || "evidence.zip"
        );
      } else {
        setSnackbarInfo({
          message: response?.message || "Download failed: no URL returned",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error downloading evidence:", error);
      setSnackbarInfo({
        message: error.message || "Download failed. Please try again.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setIsDownloadingEvidence(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: theme.customColors.background.header,
        overflow: "hidden",
      }}
    >
      {/* Back Button and Control Header */}
      <Box sx={{ px: 2, pt: 2 }}>
        {/* Back Button and Action Buttons */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              color: theme.customColors.text.secondary,
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: theme.customColors.background.hover,
              },
            }}
          >
            Back to Controls
          </Button>

          <ControlActionButtons
            currentStatus={currentStatusKey}
            redFlag={redFlag}
            statusLoading={statusLoading}
            isSubmittingStatus={isSubmittingStatus}
            isSubmittingRedFlag={isSubmittingRedFlag}
            onStatusUpdate={handleStatusUpdate}
            onRedFlagToggle={handleRedFlagToggle}
            onRemoveStatus={handleRemoveStatus}
            onCommentsClick={handleCommentsClick}
            onEvidenceRequestClick={handleEvidenceRequestClick}
            onReadyForAuditClick={handleReadyForAuditClick}
            onDownloadEvidenceClick={handleDownloadEvidence}
            isDownloadingEvidence={isDownloadingEvidence}
            isAuditorView={isAuditorView}
            commentsCount={commentsCount}
            audit={auditData}
          />
        </Box>

        {/* Control Information Card */}
        <ControlHeaderCard
          control={control}
          currentStatusKey={currentStatusKey}
          redFlag={redFlag}
          isLoading={isLoading}
          controlDetailsExpanded={controlDetailsExpanded}
          onToggleExpanded={() => setControlDetailsExpanded(!controlDetailsExpanded)}
          isAuditorView={isAuditorView}
        />
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          px: 2,
          pb: 2,
          gap: 2,
        }}
      >
        {/* Evidence Sets Sidebar */}
        <EvidenceSetSidebar
          checks={sortedChecks}
          selectedCheckType={selectedCheckType}
          onCheckSelect={handleCheckClick}
          loading={isLoading}
        />

        {/* Details - Resources Table Only */}
        <EvidenceSetDetails
          checkDetails={checkDetails}
          activeCheckId={activeCheckId}
          checkCategory={checkCategory}
          onRefetch={refetchCheckDetails}
          loading={checkLoading}
          auditEndDate={auditData?.audit_end_date}
        />
      </Box>

      {/* Comments Drawer */}
      <ControlCommentsDrawer
        open={commentsDrawerOpen}
        onClose={handleCommentsClose}
        control={control}
        audit={auditData}
        isAuditorView={isAuditorView}
      />

      {/* Evidence Request Dialog */}
      <EvidenceRequestDialog
        open={evidenceRequestDialogOpen}
        onClose={handleEvidenceRequestClose}
        onSubmit={handleEvidenceRequestSubmit}
        mode="create"
        loading={creatingEvidenceRequest}
        controlsList={control ? [{
          ...control,
          auditControlId: control.auditControlId,
          controlsTitle: control.controlsTitle || control.controlsName,
          controlsName: control.controlsName,
          controlsDescription: control.controlsDescription,
        }] : []}
      />

      {/* Ready for Audit Confirmation Dialog */}
      <ReadyForAuditDialog
        open={readyForAuditDialogOpen}
        onClose={() => setReadyForAuditDialogOpen(false)}
        onConfirm={handleReadyForAuditConfirm}
        loading={isSubmittingStatus}
      />

      {/* Snackbar */}
      {snackbarInfo && (
        <Snackbar
          open={Boolean(snackbarInfo)}
          autoHideDuration={3000}
          onClose={() => setSnackbarInfo(null)}
          anchorOrigin={{
            vertical: snackbarInfo.vertical || "top",
            horizontal: snackbarInfo.horizontal || "right",
          }}
          message={snackbarInfo.message}
        />
      )}

      {/* Loading Toast for Evidence Download */}
      <LoadingToast
        open={isDownloadingEvidence}
        title="Preparing your download"
        message="Sit back and relax - this may take a few moments."
      />
    </Box>
  );
};

export default ControlDetailsPage;
